import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { GlassCard } from '../../components/GlassCard';
import { PageHeader } from '../../components/AppLayout';
import { useUserStore } from '../../store/userStore';
import { SERVICE_TIERS } from '../../data/constants';
import { createAppointment, getAppointments, getOpenSlots, cancelAppointment } from '../../lib/api';

const STATUS_STYLE = {
  pending: { label: 'Awaiting approval', bg: 'rgba(245,158,11,0.12)', color: '#B45309', icon: 'fa-hourglass-half' },
  confirmed: { label: 'Confirmed', bg: 'rgba(16,185,129,0.12)', color: '#047857', icon: 'fa-circle-check' },
  declined: { label: 'Declined', bg: 'rgba(107,114,128,0.12)', color: '#4B5563', icon: 'fa-circle-xmark' },
  cancelled: { label: 'Cancelled', bg: 'rgba(107,114,128,0.12)', color: '#4B5563', icon: 'fa-ban' },
  completed: { label: 'Completed', bg: 'rgba(20,184,166,0.12)', color: '#0F766E', icon: 'fa-check-double' },
  scheduled: { label: 'Scheduled', bg: 'rgba(20,184,166,0.12)', color: '#0F766E', icon: 'fa-calendar-day' },
};
const fmtDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

export default function Book() {
  const user = useUserStore((s) => s.user);
  const [tier, setTier] = useState('horizon');
  const [dates, setDates] = useState([]);
  const [date, setDate] = useState('');
  const [slotId, setSlotId] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    getAppointments(user.id).then((a) => setAppointments(a || [])).catch(() => {});
    getOpenSlots().then((r) => {
      const d = r?.dates || [];
      setDates(d);
      setDate((cur) => (d.some((x) => x.date === cur) ? cur : d[0]?.date || ''));
    }).catch(() => {});
  };
  useEffect(() => {
    if (user?.id) load();
  }, [user?.id]); // eslint-disable-line

  const slotsForDate = dates.find((d) => d.date === date)?.slots || [];
  const chosen = slotsForDate.find((s) => s.id === slotId);

  const book = async () => {
    if (!chosen) {
      toast.error('Please choose an available time');
      return;
    }
    setSaving(true);
    try {
      await createAppointment({
        user_id: user.id,
        appointment_type: `Auralis ${SERVICE_TIERS.find((t) => t.key === tier).name}`,
        slot_id: chosen.id,
      });
      toast.success('Request sent — your clinician will confirm shortly');
      setSlotId('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send request. Please try again.');
      load();
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (id) => {
    try { await cancelAppointment(id); toast.success('Appointment cancelled'); load(); } catch { toast.error('Could not cancel'); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 pt-6 sm:pt-10">
      <PageHeader title="Book a consultation" subtitle="Choose the Auralis service that fits where you are." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {SERVICE_TIERS.map((t, i) => (
          <GlassCard
            key={t.key}
            hover
            onClick={() => setTier(t.key)}
            data-testid={`tier-${t.key}`}
            className={`p-6 cursor-pointer relative animate-fade-up ${tier === t.key ? 'ring-2' : ''}`}
            style={{ animationDelay: `${i * 0.05}s`, ...(tier === t.key ? { boxShadow: `0 0 0 2px ${t.color}` } : {}) }}
          >
            {t.popular && (
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wider text-white rounded-full px-3 py-1" style={{ background: t.color }}>
                Most popular
              </span>
            )}
            <div className="grid place-items-center w-11 h-11 rounded-xl mb-3" style={{ background: `${t.color}18`, color: t.color }}>
              <i className="fa-solid fa-heart-pulse" />
            </div>
            <div className="font-display text-xl">{t.name}</div>
            <div className="text-sm text-[var(--aura-ink-soft)] mb-3">{t.tagline}</div>
            <div className="flex items-baseline gap-1 mb-4">
              <span className="font-display text-3xl" style={{ color: t.color }}>{t.price}</span>
              <span className="text-xs text-[var(--aura-ink-soft)]">{t.cadence}</span>
            </div>
            <ul className="space-y-2">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <i className="fa-solid fa-check mt-0.5" style={{ color: t.color }} /> {f}
                </li>
              ))}
            </ul>
          </GlassCard>
        ))}
      </div>

      <GlassCard className="p-6 mb-8" data-testid="booking-picker">
        <h3 className="font-display text-xl mb-1">Pick a time</h3>
        <p className="text-sm text-[var(--aura-ink-soft)] mb-4">These are the times your clinicians have made available. Your request is confirmed once a clinician approves it.</p>
        {dates.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[var(--aura-line)] p-6 text-center text-[var(--aura-ink-soft)]" data-testid="booking-no-slots">
            <i className="fa-regular fa-calendar-xmark text-2xl mb-2 block" />
            No appointment times are available right now. Please check back soon or message your clinician.
          </div>
        ) : (
          <>
            <label className="block text-sm text-[var(--aura-ink-soft)] mb-2">Date</label>
            <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
              {dates.map((d) => (
                <button
                  key={d.date}
                  data-testid={`booking-date-${d.date}`}
                  onClick={() => { setDate(d.date); setSlotId(''); }}
                  className="shrink-0 rounded-xl px-4 py-2.5 text-sm border transition-base text-left"
                  style={date === d.date ? { background: 'var(--aura-purple)', borderColor: 'var(--aura-purple)', color: '#fff' } : { borderColor: 'var(--aura-line)' }}
                >
                  <div className="font-semibold">{new Date(d.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</div>
                  <div className="text-[11px] opacity-70">{d.slots.length} time{d.slots.length === 1 ? '' : 's'}</div>
                </button>
              ))}
            </div>
            <label className="block text-sm text-[var(--aura-ink-soft)] mb-2">Time{date ? ` on ${fmtDate(date)}` : ''}</label>
            <div className="flex flex-wrap gap-2">
              {slotsForDate.map((s) => (
                <button
                  key={s.id}
                  data-testid={`slot-${s.time}`}
                  onClick={() => setSlotId(s.id)}
                  className="rounded-xl px-4 py-2.5 text-sm border transition-base text-left"
                  style={slotId === s.id ? { background: 'var(--aura-purple)', borderColor: 'var(--aura-purple)', color: '#fff' } : { borderColor: 'var(--aura-line)' }}
                >
                  <div className="font-semibold">{s.time}</div>
                  <div className="text-[11px] opacity-70">{s.clinician_name}</div>
                </button>
              ))}
            </div>
            <button
              data-testid="confirm-booking-btn"
              onClick={book}
              disabled={saving || !chosen}
              className="mt-6 aura-btn-gradient text-white font-semibold rounded-2xl px-6 py-3.5 flex items-center gap-2 disabled:opacity-60"
            >
              {saving ? <i className="fa-solid fa-circle-notch fa-spin" /> : <><i className="fa-solid fa-paper-plane" /> Request appointment{chosen ? ` · ${chosen.time} with ${chosen.clinician_name}` : ''}</>}
            </button>
          </>
        )}
      </GlassCard>

      {appointments.length > 0 && (
        <div className="mb-8" data-testid="my-appointments">
          <h3 className="font-display text-xl mb-3">Your appointments</h3>
          <div className="flex flex-col gap-3">
            {appointments.map((a) => {
              const st = STATUS_STYLE[a.status] || STATUS_STYLE.pending;
              return (
                <GlassCard key={a.id} className="p-4 flex items-center gap-4" data-testid={`appointment-${a.id}`}>
                  <div className="grid place-items-center w-11 h-11 rounded-xl" style={{ background: st.bg, color: st.color }}>
                    <i className={`fa-solid ${st.icon}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{a.appointment_type}</div>
                    <div className="text-sm text-[var(--aura-ink-soft)]">{fmtDate(a.date)} at {a.time_slot}{a.provider_name ? ` · ${a.provider_name}` : ''}</div>
                    {a.status === 'pending' ? <div className="text-xs text-[var(--aura-ink-soft)] mt-0.5">We'll let you know as soon as your clinician confirms.</div> : null}
                    {a.decision_note ? <div className="text-xs text-[var(--aura-ink-soft)] mt-0.5 italic">"{a.decision_note}"</div> : null}
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <span className="text-xs font-semibold rounded-full px-3 py-1" style={{ background: st.bg, color: st.color }} data-testid={`appointment-status-${a.id}`}>{st.label}</span>
                    {['pending', 'confirmed'].includes(a.status) ? (
                      <button data-testid={`appointment-cancel-${a.id}`} onClick={() => cancel(a.id)} className="text-[11px] text-[var(--aura-ink-soft)] hover:text-red-600">Cancel</button>
                    ) : null}
                  </div>
                </GlassCard>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
